import { Injectable, OnDestroy } from '@angular/core';
import {
  ApproveESVRInfoUpdate,
  BatchStatus,
  BatchViewModel,
  DispatchESVRInfoUpdate,
  DisputeESVRCommand,
  EditESVRCommand,
  ESVRFillSiteInfoEditCommand,
  ESVRFillSiteSaveCommand,
  ESVRsDownloadQuery,
  ESVRSearchViewModelESVRTotalTypePagedResultResponse,
  ESVRSourceSiteInfoEditCommand,
  ESVRSourceSiteSaveCommand,
  ESVRsReqDownloadQuery,
  ESVRViewModel,
  ForwardESVRsCommand,
  IBatchHistoryViewModel,
  IBatchReconciliationViewModel,
  PrepareESVRCommand,
  RejectESVRCommand
} from '@services/cleanfill-api/cleanfill-api.generated';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { BehaviorSubject, forkJoin, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BatchDetailService } from './batch-detail.service';

@Injectable({ providedIn: 'root' })
export class BatchDetailStore implements OnDestroy {
  batchId: string;
  name: string;

  private batchSubject: BehaviorSubject<BatchViewModel>;
  batch$: Observable<BatchViewModel>;
  private loadsSubject = new BehaviorSubject<ESVRSearchViewModelESVRTotalTypePagedResultResponse>(null);
  loads$ = this.loadsSubject.asObservable().pipe(map((payload) => payload?.records || []));
  // tslint:disable-next-line:no-any
  public esvrSubject: BehaviorSubject<any>;
  _esvrIdSubject$ = new BehaviorSubject<string>(null);
  esvrId$ = this._esvrIdSubject$.asObservable();

  esvr$: Observable<ESVRViewModel>;
  private summarySubject: BehaviorSubject<IBatchReconciliationViewModel>;
  summary$: Observable<IBatchReconciliationViewModel>;

  private historySubject: BehaviorSubject<IBatchHistoryViewModel[]>;
  history$: Observable<IBatchHistoryViewModel[]>;
  loadsTotal$: Observable<number>;
  subscriptions = new Subscription();

  get batch() {
    return this.batchSubject.value;
  }
  get loads() {
    return this.loadsSubject.value;
  }
  get summary() {
    return this.summarySubject.value;
  }
  get esvr() {
    return this.esvrSubject.value;
  }
  set esvrId(value: string) {
    this._esvrIdSubject$.next(value);
  }
  get esvrId() {
    return this._esvrIdSubject$.value;
  }

  constructor(private batchDetailService: BatchDetailService) {
    this.batchSubject = new BehaviorSubject<BatchViewModel>(null);
    this.batch$ = this.batchSubject.asObservable().pipe(filter((t) => t !== null));

    this.esvrSubject = new BehaviorSubject<ESVRViewModel>(null);
    this.esvr$ = this.esvrSubject.asObservable();
    this.summarySubject = new BehaviorSubject<IBatchReconciliationViewModel>(null);
    this.summary$ = this.summarySubject.asObservable().pipe(filter((t) => t !== null));

    this.historySubject = new BehaviorSubject<IBatchHistoryViewModel[]>(null);
    this.history$ = this.historySubject.asObservable().pipe(filter((t) => t !== null));
    this.loadsTotal$ = this.batch$.pipe(map((batch) => batch.maxEsvrIndex));
  }

  init(batchId: string) {
    this.batchId = batchId;
    this.refresh();
  }

  refresh() {
    return forkJoin([this.refreshBatch(), this.refreshSummary(), this.refreshHistory()]).toPromise();
  }
  refreshHistory() {
    return this.batchDetailService
      .getBatchHistory(this.batchId)
      .toPromise()
      .then((t) => this.historySubject.next(t));
  }
  refreshBatch() {
    return this.batchDetailService
      .getBatchDetail(this.batchId)
      .toPromise()
      .then((t) => {
        this.batchSubject.next(t);
        this.name = t.name;
      });
  }
  refreshSummary() {
    return this.batchDetailService
      .getBatchSummary(this.batchId)
      .toPromise()
      .then((t) => {
        this.summarySubject.next(t);
      });
  }

  search(query: ISearchQuery) {
    return this.batchDetailService.searchESVR(query).subscribe((t) => {
      this.loadsSubject.next(t);
    });
  }

  loadESVR(id: string, fn = (es: ESVRViewModel) => { }): void {
    const sub = this.batchDetailService.loadESVR(id).subscribe((esvr) => {
      this.esvrSubject.next(esvr);
      fn(esvr);
    });
    this.subscriptions.add(sub);
  }

  clearESVRs() {
    this.loadsSubject.next(null);
  }

  exportToExcel(query: ISearchQuery) {
    return this.batchDetailService.exportToExcel(query);
  }

  exportBatchLoadsToExcel(query: ISearchQuery) {
    return this.batchDetailService.exportBatchLoadsToExcel(query);
  }

  downloadESVRs(body: ESVRsDownloadQuery) {
    return this.batchDetailService.downloadESVRs(body);
  }
  downloadTrucksBilling(body: ESVRsReqDownloadQuery) {
    return this.batchDetailService.downloadTrucksBilling(body);
  }

  prepareESVRs(loads: PrepareESVRCommand, fn = () => { }) {
    return this.batchDetailService.prepareESVRs(loads).subscribe(fn);
  }

  forwardESVRs(loads: ForwardESVRsCommand, fn = () => { }) {
    return this.batchDetailService.forwardESVRs(loads).subscribe(fn);
  }

  forwardPreparedESVR(esvrUniqueId: string, fn = () => { }) {
    return this.batchDetailService.forwardPreparedESVR(esvrUniqueId).subscribe(fn);
  }

  dispatchESVR(esvrUniqueId: string, body: DispatchESVRInfoUpdate, fn = () => { }) {
    return this.batchDetailService.dispatchESVR(esvrUniqueId, body).subscribe(fn);
  }
  saveESVRSource(esvrUniqueId: string, body: ESVRSourceSiteSaveCommand, fn = () => { }) {
    return this.batchDetailService.saveESVRSource(esvrUniqueId, body).subscribe(fn);
  }
  saveDispatchedESVRSource(sourceSiteUniqueId: string, esvrUniqueId: string, body: ESVRSourceSiteInfoEditCommand, fn = () => { }) {
    return this.batchDetailService.saveDispatchedESVRSource(sourceSiteUniqueId, esvrUniqueId, body).subscribe(fn);
  }
  saveESVRFill(esvrUniqueId: string, body: ESVRFillSiteSaveCommand, fn = () => { }) {
    return this.batchDetailService.saveESVRFill(esvrUniqueId, body).subscribe(fn);
  }
  saveRecievedESVRFill(fillSiteUniqueId: string, esvrUniqueId: string, body: ESVRFillSiteInfoEditCommand, fn = () => { }) {
    return this.batchDetailService.saveRecievedESVRFill(fillSiteUniqueId, esvrUniqueId, body).subscribe(fn);
  }

  saveESVR(esvrUniqueId: string, body: EditESVRCommand, fn = () => { }) {
    return this.batchDetailService.saveESVR(esvrUniqueId, body).subscribe(fn);
  }
  acceptESVR(esvrUniqueId: string, body: ApproveESVRInfoUpdate, fn = () => { }) {
    return this.batchDetailService.acceptESVR(esvrUniqueId, body).subscribe(fn);
  }
  rejectESVR(esvrUniqueId: string, body: RejectESVRCommand, fn = () => { }) {
    return this.batchDetailService.rejectESVR(esvrUniqueId, body).subscribe(fn);
  }
  holdESVR(esvrUniqueId: string, body: DisputeESVRCommand, fn = () => { }) {
    return this.batchDetailService.holdESVR(esvrUniqueId, body).subscribe(fn);
  }

  releaseESVRs(batchId: string, fn = () => { }) {
    return this.batchDetailService.releaseESVRs(batchId).subscribe(fn);
  }

  notifyESVRsReleased(batchId: string, email: string, fn = () => { }) {
    return this.batchDetailService.notifyESVRsReleased(batchId, email).subscribe(fn);
  }

  addAdditionalESVRs(batchId: string, estimatedLoads: number, estimatedWeight: number, estimatedVolume: number, fn = () => { }) {
    return this.batchDetailService.addAdditionalESVRs(batchId, estimatedLoads, estimatedWeight, estimatedVolume).subscribe(fn);
  }

  completeBatch(batchId: string, fn = () => { }) {
    return this.batchDetailService.updateBatchStatus(batchId, BatchStatus.Complete).subscribe(fn);
  }

  archiveBatch(batchId: string, fn = () => { }) {
    return this.batchDetailService.updateBatchStatus(batchId, BatchStatus.Archive).subscribe(fn);
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
